import { Backdrop, Box, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const C = 32;
const GHOST = 'var(--cta_bg)';
const MAIN = 'var(--cta_primary)';

const ICON_SIZE = '52px';
const CYCLE_MS = 2000;

// ─────────────────────────────────────────────────────────────
// Clock Icon
// ─────────────────────────────────────────────────────────────

function ClockIcon() {
  const r = 22;
  const circ = 2 * Math.PI * r;

  const ticks = [0, 90, 180, 270].map((deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;

    return {
      x1: C + (r - 5) * Math.cos(rad),
      y1: C + (r - 5) * Math.sin(rad),
      x2: C + r * Math.cos(rad),
      y2: C + r * Math.sin(rad),
    };
  });

  const hourRad = ((300 - 90) * Math.PI) / 180;

  const hourX = C + 11 * Math.cos(hourRad);
  const hourY = C + 11 * Math.sin(hourRad);

  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 64 64"
      preserveAspectRatio="xMidYMid meet"
    >
      <g stroke={GHOST} fill="none" strokeLinecap="round">
        <circle cx={C} cy={C} r={r} strokeWidth={2} />

        {ticks.map((t, i) => (
          <line key={i} {...t} strokeWidth={2} />
        ))}

        <line x1={C} y1={C} x2={hourX} y2={hourY} strokeWidth={2.5} />

        <line x1={C} y1={C} x2={C} y2={C - 16} strokeWidth={2} />

        <circle cx={C} cy={C} r={2.5} fill={GHOST} stroke="none" />
      </g>

      <motion.circle
        cx={C}
        cy={C}
        r={r}
        fill="none"
        stroke={MAIN}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={circ}
        transform={`rotate(-90 ${C} ${C})`}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: 0 }}
        transition={{
          duration: 1.6,
          ease: 'easeInOut',
        }}
      />

      <motion.g
        stroke={MAIN}
        fill="none"
        strokeLinecap="round"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.35,
          delay: 1.1,
        }}
      >
        <line x1={C} y1={C} x2={hourX} y2={hourY} strokeWidth={2.5} />

        <line x1={C} y1={C} x2={C} y2={C - 16} strokeWidth={2} />

        <circle cx={C} cy={C} r={2.5} fill={MAIN} stroke="none" />
      </motion.g>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Graph Icon
// ─────────────────────────────────────────────────────────────

function GraphIcon() {
  const baseline = 50;

  const bars = [
    { x: 11, w: 12, h: 26, delay: 0.05 },
    { x: 27, w: 12, h: 38, delay: 0.25 },
    { x: 43, w: 12, h: 30, delay: 0.45 },
  ];

  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 64 64"
      preserveAspectRatio="xMidYMid meet"
    >
      <g fill={GHOST}>
        {bars.map((b, i) => (
          <rect key={i} x={b.x} y={baseline - b.h} width={b.w} height={b.h} rx={2} />
        ))}
      </g>

      <line x1={8} y1={baseline} x2={56} y2={baseline} stroke={GHOST} strokeWidth={2} />

      <line x1={8} y1={baseline} x2={56} y2={baseline} stroke={MAIN} strokeWidth={2} />

      {bars.map((b, i) => (
        <motion.g key={i} transform={`translate(${b.x + b.w / 2} ${baseline})`}>
          <motion.rect
            x={-b.w / 2}
            y={-b.h}
            width={b.w}
            height={b.h}
            rx={2}
            fill={MAIN}
            style={{
              transformOrigin: '50% 100%',
            }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{
              duration: 0.65,
              delay: b.delay,
              ease: [0.34, 1.56, 0.64, 1],
            }}
          />
        </motion.g>
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Receipt Icon
// ─────────────────────────────────────────────────────────────

function ReceiptIcon() {
  const px = 18;
  const py = 10;
  const pw = 28;
  const ph = 44;
  const pr = 2;

  const perim = 2 * (pw + ph - 4 * pr) + 2 * Math.PI * pr;

  const paper = [
    `M ${px + pr} ${py}`,
    `L ${px + pw - pr} ${py}`,
    `A ${pr} ${pr} 0 0 1 ${px + pw} ${py + pr}`,
    `L ${px + pw} ${py + ph - pr}`,
    `A ${pr} ${pr} 0 0 1 ${px + pw - pr} ${py + ph}`,
    `L ${px + pr} ${py + ph}`,
    `A ${pr} ${pr} 0 0 1 ${px} ${py + ph - pr}`,
    `L ${px} ${py + pr}`,
    `A ${pr} ${pr} 0 0 1 ${px + pr} ${py}`,
    'Z',
  ].join(' ');

  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 64 64"
      preserveAspectRatio="xMidYMid meet"
    >
      <path d={paper} stroke={GHOST} fill="none" strokeWidth={2} />

      <text
        x={C}
        y={38}
        textAnchor="middle"
        fontSize={20}
        fontWeight="700"
        fill={GHOST}
        fontFamily="sans-serif"
      >
        $
      </text>

      <motion.path
        d={paper}
        fill="none"
        stroke={MAIN}
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={perim}
        initial={{ strokeDashoffset: perim }}
        animate={{ strokeDashoffset: 0 }}
        transition={{
          duration: 1,
          ease: 'easeInOut',
        }}
      />

      <motion.text
        x={C}
        y={38}
        textAnchor="middle"
        fontSize={20}
        fontWeight="700"
        fill={MAIN}
        fontFamily="sans-serif"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 0.4,
          delay: 0.9,
        }}
      >
        $
      </motion.text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Loader
// ─────────────────────────────────────────────────────────────

const ICONS = [ClockIcon, GraphIcon, ReceiptIcon];

export function IconCycleLoader() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % ICONS.length);
    }, CYCLE_MS);

    return () => clearInterval(id);
  }, []);

  const Icon = ICONS[current];

  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: 'rgba(255,255,255,0.45)',
        backdropFilter: 'blur(3px)',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        <Box
          sx={{
            position: 'relative',
            height: ICON_SIZE,
            width: ICON_SIZE,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              initial={{
                opacity: 0,
                scale: 0.82,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 1.12,
              }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
            >
              <Icon />
            </motion.div>
          </AnimatePresence>
        </Box>

        <Typography
          variant="subtitle1"
          sx={{
            color: MAIN,
            lineHeight: 1,
          }}
        >
          Loading...
        </Typography>
      </Box>
    </Backdrop>
  );
}
